<template>
  <div class="conv-advanced-filter">
    <woot-modal-header-v2
      v-if="!skipHeader"
      :header-title="filterModalHeaderTitle"
      :header-content="filterModalSubTitle"
    >
      <template #titleIcon>
        <span class="material-symbols-outlined text-icon-primary">
          filter_alt
        </span>
      </template>
    </woot-modal-header-v2>

    <div class="px-6 pt-4 w-full">
      <div class="tabs flex flex-row items-center w-full">
        <div
          class="tab-item px-6 py-5 flex flex-row gap-2.5 border-b cursor-pointer"
          :class="{ active: activeTab === 'new_filter' }"
          @click="selectTab('new_filter')"
        >
          <span class="material-symbols-outlined">filter_alt</span>
          <span>{{ $t('FILTER.NEW_FILTER') }}</span>
        </div>
        <div
          class="tab-item px-6 py-5 flex flex-row gap-2.5 border-b cursor-pointer"
          :class="{ active: activeTab === 'saved_filters' }"
          @click="selectTab('saved_filters')"
        >
          <span class="material-symbols-outlined">bookmark</span>
          <span>{{ $t('FILTER.SAVED_FILTERS') }}</span>
        </div>
      </div>
    </div>

    <div
      v-if="activeTab === 'new_filter'"
      ref="filtersBody"
      class="filters-body px-6 py-4 min-h-[200px] max-h-[600px]"
      :class="{ 'overflow-y-auto': isOverflowing }"
    >
      <div v-if="isFolderView">
        <label
          class="input-label text-[#1F2937] mb-1.5 leading-5 font-[400]"
          :class="{ error: !activeFolderNewName }"
        >
          {{ $t('FILTER.FOLDER_LABEL') }}
        </label>
        <input v-model="activeFolderNewName" type="text" class="folder-input" />
        <span v-if="!activeFolderNewName" class="message">
          {{ $t('FILTER.EMPTY_VALUE_ERROR') }}
        </span>
        <label class="mb-1.5 text-[#1F2937] leading-5 font-[400]">
          {{ $t('FILTER.FOLDER_QUERY_LABEL') }}
        </label>
      </div>
      <filter-input-box
        v-for="(filter, i) in appliedFilters"
        :key="i"
        v-model="appliedFilters[i]"
        :filter-groups="filterGroups"
        :input-type="
          getInputType(
            appliedFilters[i].attribute_key,
            appliedFilters[i].filter_operator
          )
        "
        :operators="getOperators(appliedFilters[i].attribute_key)"
        :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
        :show-query-operator="i !== appliedFilters.length - 1"
        :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
        :grouped-filters="true"
        :show-input-label="true"
        :v="$v.appliedFilters.$each[i]"
        :disable-remove-filter="appliedFilters.length === 1"
        @resetFilter="resetFilter(i, appliedFilters[i])"
        @removeFilter="removeFilter(i)"
      />
      <div class="mt-4">
        <button
          class="button-outline px-5 py-[11px] flex items-center justify-center gap-1"
          @click="appendNewFilter"
        >
          <span class="material-symbols-outlined">add</span>
          <span class="text-base">{{ $t('FILTER.ADD_CONDITION') }}</span>
        </button>
      </div>
    </div>

    <div
      v-if="activeTab === 'new_filter'"
      class="px-6 py-4 flex flex-row items-center justify-between gap-2 border-t border-gray-100 min-h-[76px]"
    >
      <div class="flex flex-row items-center gap-2">
        <input
          id="save-filter-checkbox"
          type="checkbox"
          class="semantic-checkbox"
          @change="onChangeSaveFitler($event)"
        />
        <label for="save-filter-checkbox" class="!mx-0">
          <span class="text-[#1F2937]">
            {{ $t('FILTER.SAVE_THIS_FILTER') }}
          </span>
        </label>
      </div>
      <div v-if="isSaveFitler" class="flex flex-col items-start gap-1.5 w-3/5">
        <div class="text-sm">
          {{ $t('FILTER.FILTER_NAME') }}
          <span class="text-green-500 text-sm">*</span>
        </div>
        <input
          v-model.trim="filterName"
          type="text"
          class="rounded-[8px] border-[#D4D4D4] h-[44px] mb-0 px-3 py-[10px] hover:border-gray-400 focus:border-gray-400"
          :placeholder="$t('FILTER.ENTER_NAME')"
          @input="$v.filterName.$touch"
        />
      </div>
    </div>

    <div
      v-if="activeTab === 'saved_filters'"
      class="saved-filters-body px-6 pt-4 pb-[34px] w-full"
    >
      <template v-if="!editingCustomViewId">
        <div class="text-sm font-semibold text-gray-500 mb-3">
          {{ $t('FILTER.SAVED_FILTERS') }}
        </div>
        <div v-if="customViews.length">
          <div
            v-for="customView in customViews"
            :key="customView.id"
            class="saved-filter-row"
            :class="{ selected: selectedCustomViewId === customView.id }"
            @click="selectCustomView(customView)"
          >
            <div class="flex flex-row items-center gap-1">
              <span class="material-symbols-outlined text-gray-600">
                bookmark
              </span>
              <span>{{ customView.name }}</span>
            </div>
            <span
              class="material-symbols-outlined text-gray-600 cursor-pointer rounded-[4px] hover:bg-gray-100 select-none"
              @click="openCustomViewMenu($event, customView)"
            >
              more_vert
            </span>
            <woot-context-menu
              v-if="openMenuCustomViewId === customView.id"
              :x="contextMenuPosition.x"
              :y="contextMenuPosition.y"
              @close="closeCustomViewMenu"
            >
              <div class="menu-container" @click.stop>
                <menu-item
                  :option="{
                    icon: 'edit',
                    label: $t('FILTER.CUSTOM_VIEWS.ROW_ACTIONS.EDIT'),
                  }"
                  variant="icon"
                  @click="onEditCustomView(customView)"
                />
                <menu-item
                  :option="{
                    icon: 'content_copy',
                    label: $t('FILTER.CUSTOM_VIEWS.ROW_ACTIONS.DUPLICATE'),
                  }"
                  variant="icon"
                  @click="onDuplicateCustomView(customView)"
                />
                <div class="danger-item">
                  <menu-item
                    :option="{
                      icon: 'delete',
                      label: $t('FILTER.CUSTOM_VIEWS.ROW_ACTIONS.DELETE'),
                      iconColor: 'text-[#EF4444]',
                      labelColor: 'text-[#DC2626]',
                    }"
                    variant="icon"
                    @click="onDeleteCustomView(customView)"
                  />
                </div>
              </div>
            </woot-context-menu>
          </div>
        </div>
        <div v-else class="text-sm text-center text-gray-500">
          {{ $t('FILTER.NO_SAVED_FILTERS') }}
        </div>
      </template>

      <template v-else>
        <div
          class="flex flex-row items-center gap-1 py-3 text-green-600 cursor-pointer w-fit mb-3"
          @click="backToSavedFiltersList"
        >
          <span class="material-symbols-outlined">chevron_left</span>
          <span>{{ $t('FILTER.CUSTOM_VIEWS.ROW_ACTIONS.BACK_TO_LIST') }}</span>
        </div>
        <div class="text-base font-semibold text-gray-600 mb-4">
          {{ $t('FILTER.SAVED_FILTERS') }}
        </div>
        <label
          class="input-label text-[#1F2937] mb-1.5 leading-5 font-[400]"
          :class="{ error: !activeFolderNewName }"
        >
          {{ $t('FILTER.FILTER_NAME') }}
        </label>
        <input
          v-model="activeFolderNewName"
          type="text"
          class="w-full rounded-[8px] border-gray-300 h-[44px] mb-4 px-3 py-[10px] hover:border-gray-400 focus:border-gray-400"
        />
        <span v-if="!activeFolderNewName" class="message">
          {{ $t('FILTER.EMPTY_VALUE_ERROR') }}
        </span>
        <filter-input-box
          v-for="(filter, i) in appliedFilters"
          :key="i"
          v-model="appliedFilters[i]"
          :filter-groups="filterGroups"
          :input-type="
            getInputType(
              appliedFilters[i].attribute_key,
              appliedFilters[i].filter_operator
            )
          "
          :operators="getOperators(appliedFilters[i].attribute_key)"
          :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
          :show-query-operator="i !== appliedFilters.length - 1"
          :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
          :grouped-filters="true"
          :show-input-label="true"
          :v="$v.appliedFilters.$each[i]"
          :disable-remove-filter="appliedFilters.length === 1"
          @resetFilter="resetFilter(i, appliedFilters[i])"
          @removeFilter="removeFilter(i)"
        />
        <div class="mt-4">
          <button
            class="button-outline px-5 py-[11px] flex items-center justify-center gap-1"
            @click="appendNewFilter"
          >
            <span class="material-symbols-outlined">add</span>
            <span class="text-base">{{ $t('FILTER.ADD_CONDITION') }}</span>
          </button>
        </div>
      </template>
    </div>

    <delete-custom-views
      v-if="showDeleteConfirmModal"
      :show-delete-popup.sync="showDeleteConfirmModal"
      :active-custom-view="customViewPendingDeletion"
      :custom-views-id="
        customViewPendingDeletion && customViewPendingDeletion.id
      "
      :active-filter-type="0"
      @close="closeDeleteConfirmModal"
    />

    <woot-modal
      v-if="showDuplicateModal"
      :show.sync="showDuplicateModal"
      :on-close="closeDuplicateModal"
      class="duplicate-filter-modal"
    >
      <woot-modal-header-v2
        :header-title="$t('FILTER.CUSTOM_VIEWS.DUPLICATE.TITLE')"
        header-title-icon="content_copy"
      />
      <div class="px-6 pt-4 flex flex-col gap-1.5">
        <label class="input-label text-[#1F2937] leading-5 font-[400] !mb-0">
          {{ $t('FILTER.FILTER_NAME') }}
        </label>
        <input
          ref="duplicateNameInput"
          v-model.trim="duplicateName"
          type="text"
          class="w-full rounded-[8px] border-gray-300 h-[44px] mb-0 px-3 py-[10px] hover:border-gray-400 focus:border-gray-400"
          @keyup.enter="confirmDuplicateCustomView"
        />
      </div>
      <div class="px-6 pb-6 pt-[28px] flex flex-row justify-end gap-3">
        <woot-button
          class="button clear text-base px-5 py-3 leading-[20px] h-[48px] font-[400]"
          color-scheme="secondary"
          @click.prevent="closeDuplicateModal"
        >
          {{ $t('FILTER.CUSTOM_VIEWS.DUPLICATE.CANCEL') }}
        </woot-button>
        <woot-button
          class="button-filled text-base px-5 py-3 font-[400] h-[48px]"
          color-scheme=""
          :is-loading="isDuplicating"
          :disabled="!duplicateName"
          @click.prevent="confirmDuplicateCustomView"
        >
          {{ $t('FILTER.CUSTOM_VIEWS.DUPLICATE.CONFIRM') }}
        </woot-button>
      </div>
    </woot-modal>

    <div
      v-if="!skipButtons"
      class="w-full px-6 pt-4 pb-6 z-[60] border-t border-gray-100"
      style="box-shadow: 0px -1px 4px #cacaca29"
    >
      <div class="flex flex-row justify-end gap-3 px-0 w-full">
        <woot-button
          class="cancel-filter-btn button clear text-base px-5 py-3"
          color-scheme="secondary"
          @click.prevent="onClose"
        >
          {{ $t('FILTER.CANCEL_BUTTON_LABEL') }}
        </woot-button>
        <woot-button
          v-if="editingCustomViewId"
          class="apply-filter-btn button-filled text-base px-5 py-3"
          color-scheme=""
          :disabled="!activeFolderNewName"
          @click="updateSavedCustomViews"
        >
          {{ $t('FILTER.CUSTOM_VIEWS.ROW_ACTIONS.SAVE_CHANGES') }}
        </woot-button>
        <woot-button
          v-else-if="isFolderView"
          class="apply-filter-btn button-filled text-base px-5 py-3"
          color-scheme=""
          :disabled="!activeFolderNewName"
          @click="updateSavedCustomViews"
        >
          {{ $t('FILTER.UPDATE_BUTTON_LABEL') }}
        </woot-button>
        <woot-button
          v-else
          class="apply-filter-btn button-filled text-base px-5 py-3"
          color-scheme=""
          :disabled="isApplyDisabled"
          @click="submitFilterQuery"
        >
          {{ $t('FILTER.APPLY_BUTTON_LABEL') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import FilterInputBox from '../FilterInput/IndexV2.vue';
import languages from './advancedFilterItems/languages';
import countries from 'shared/constants/countries.js';
import { mapGetters } from 'vuex';
import { filterAttributeGroups } from './advancedFilterItems';
import filterMixin from 'shared/mixins/filterMixin';
import * as OPERATORS from 'dashboard/components/widgets/FilterInput/FilterOperatorTypes.js';
import { CONVERSATION_EVENTS } from '../../../helper/AnalyticsHelper/events';
import MenuItem from './contextMenu/menuItem.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator.js';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';

export default {
  components: {
    FilterInputBox,
    MenuItem,
    DeleteCustomViews,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    initialFilterTypes: {
      type: Array,
      default: () => [],
    },
    initialAppliedFilters: {
      type: Array,
      default: () => [],
    },
    activeFolderName: {
      type: String,
      default: '',
    },
    isFolderView: {
      type: Boolean,
      default: false,
    },
    skipHeader: {
      type: Boolean,
      default: false,
    },
    skipButtons: {
      type: Boolean,
      default: false,
    },
    skipShowFilters: {
      type: Boolean,
      default: false,
    },
  },
  validations: {
    appliedFilters: {
      required,
      $each: {
        values: {
          ensureBetween0to999(value, prop) {
            if (prop.filter_operator === 'days_before') {
              return parseInt(value, 10) > 0 && parseInt(value, 10) < 999;
            }
            return true;
          },
          required: requiredIf(prop => {
            return !(
              prop.filter_operator === 'is_present' ||
              prop.filter_operator === 'is_not_present'
            );
          }),
        },
      },
    },
    filterName: {
      required: requiredIf(model => model.isSaveFitler),
    },
  },
  data() {
    return {
      show: true,
      appliedFilters: this.initialAppliedFilters,
      activeFolderNewName: this.activeFolderName,
      filterTypes: this.initialFilterTypes,
      filterAttributeGroups,
      filterGroups: [],
      allCustomAttributes: [],
      attributeModel: 'conversation_attribute',
      filtersFori18n: 'FILTER',
      isOverflowing: false,
      activeTab: 'new_filter',
      isSaveFitler: false,
      filterName: null,
      openMenuCustomViewId: null,
      contextMenuPosition: { x: 0, y: 0 },
      editingCustomViewId: null,
      showDeleteConfirmModal: false,
      customViewPendingDeletion: null,
      selectedCustomViewId: null,
      showDuplicateModal: false,
      customViewPendingDuplication: null,
      duplicateName: '',
      isDuplicating: false,
    };
  },
  computed: {
    ...mapGetters({
      getAppliedConversationFilters: 'getAppliedConversationFilters',
      currentUser: 'getCurrentUser',
    }),
    filterModalHeaderTitle() {
      return !this.isFolderView
        ? this.$t('FILTER.TITLE')
        : this.$t('FILTER.EDIT_CUSTOM_FILTER');
    },
    filterModalSubTitle() {
      return !this.isFolderView
        ? this.$t('FILTER.SUBTITLE')
        : this.$t('FILTER.CUSTOM_VIEWS_SUBTITLE');
    },
    customViews() {
      return this.$store.getters['customViews/getCustomViewsByFilterType'](
        'conversation'
      );
    },
    isApplyDisabled() {
      if (this.activeTab === 'saved_filters') {
        return !this.selectedCustomViewId;
      }
      return this.$v.$invalid;
    },
  },
  watch: {
    appliedFilters: {
      handler(value) {
        this.$emit('onFiltersChange', value);
      },
    },
    customViews(views) {
      const isSelectionStale =
        this.selectedCustomViewId &&
        !views.some(view => view.id === this.selectedCustomViewId);
      if (isSelectionStale) {
        this.selectedCustomViewId = null;
      }
    },
  },
  mounted() {
    this.setFilterAttributes();
    this.$store.dispatch('campaigns/get');
    if (this.getAppliedConversationFilters.length && !this.skipShowFilters) {
      this.appliedFilters = [];
      this.appliedFilters = [...this.getAppliedConversationFilters];
    } else if (!this.isFolderView) {
      // d99d commented out wait when they fix this
      // this.appliedFilters.push({
      //   attribute_key: 'status',
      //   filter_operator: 'equal_to',
      //   values: '',
      //   query_operator: 'and',
      //   attribute_model: 'standard',
      // });
    }
  },
  methods: {
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        case 'text':
          return 'plain_text';
        case 'list':
          return 'search_select';
        case 'checkbox':
          return 'search_select';
        default:
          return 'plain_text';
      }
    },
    getAttributeModel(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type.attributeModel;
    },
    getInputType(key, operator) {
      if (key === 'created_at' || key === 'last_activity_at')
        if (operator === 'days_before') return 'plain_text';
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.inputType;
    },
    getOperators(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.filterOperators;
    },
    getDropdownValues(type) {
      const statusFilters = this.$t(
        'CHAT_LIST.CHAT_STATUS_ADVANCED_FILTER_ITEMS'
      ); // d99d
      const allCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ](this.attributeModel);

      const isCustomAttributeCheckbox = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type &&
          attr.attribute_display_type === 'checkbox'
        );
      });

      if (isCustomAttributeCheckbox) {
        return [
          {
            id: true,
            name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
          },
          {
            id: false,
            name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
          },
        ];
      }

      const isCustomAttributeList = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type && attr.attribute_display_type === 'list'
        );
      });

      if (isCustomAttributeList) {
        return allCustomAttributes
          .find(attr => attr.attribute_key === type)
          .attribute_values.map(item => {
            return {
              id: item,
              name: item,
            };
          });
      }

      switch (type) {
        case 'status':
          return [
            ...Object.keys(statusFilters).map(status => {
              return {
                id: status,
                name: statusFilters[status].TEXT,
              };
            }),
          ];
        case 'assignee_id':
          return this.$store.getters['agents/getAgents'];
        case 'contact':
          return this.$store.getters['contacts/getContacts'];
        case 'inbox_id':
          return this.$store.getters['inboxes/getInboxes'];
        case 'team_id':
          return this.$store.getters['teams/getTeams'];
        case 'campaign_id':
          return this.$store.getters['campaigns/getAllCampaigns'].map(i => {
            return {
              id: i.database_id,
              name: i.title,
            };
          });
        case 'labels':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.title,
              name: i.title,
            };
          });
        case 'browser_language':
          return languages;
        case 'country_code':
          return countries;
        default:
          return undefined;
      }
    },
    appendNewFilter() {
      if (this.isFolderView || this.editingCustomViewId) {
        this.setQueryOperatorOnLastQuery();
      } else {
        this.appliedFilters.push({
          attribute_key: 'status',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      }
      this.checkOverflow();
    },
    setQueryOperatorOnLastQuery() {
      const lastItemIndex = this.appliedFilters.length - 1;
      this.appliedFilters[lastItemIndex] = {
        ...this.appliedFilters[lastItemIndex],
        query_operator: 'and',
      };
      this.$nextTick(() => {
        this.appliedFilters.push({
          attribute_key: 'status',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      });
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
        this.checkOverflow();
      }
    },
    checkOverflow() {
      const el = this.$refs.filtersBody;
      if (el) {
        this.isOverflowing =
          el.scrollHeight > 500 && this.appliedFilters.length > 1;
      }
    },
    isFiltersValid() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    selectCustomView(customView) {
      this.selectedCustomViewId =
        this.selectedCustomViewId === customView.id ? null : customView.id;
    },
    applySelectedCustomView() {
      const customViewId = this.selectedCustomViewId;
      if (!customViewId) return;
      this.onClose();
      this.$router.push({
        name: 'folder_conversations',
        params: { id: customViewId },
      });
    },
    async submitFilterQuery() {
      if (this.activeTab === 'saved_filters') {
        this.applySelectedCustomView();
        return;
      }
      if (!this.isFiltersValid) return;
      if (this.isSaveFitler) {
        try {
          await this.$store.dispatch('customViews/create', {
            name: this.filterName,
            filter_type: 'conversation',
            query: filterQueryGenerator(this.appliedFilters),
          });
          this.showAlert(
            this.$t('FILTER.CUSTOM_VIEWS.ADD.API_FOLDERS.SUCCESS_MESSAGE')
          );
          const savedCustomView = this.customViews[this.customViews.length - 1];
          this.onClose();
          this.$router.push({
            name: 'folder_conversations',
            params: { id: savedCustomView.id },
          });
          return;
        } catch (error) {
          this.showAlert(
            this.$t('FILTER.CUSTOM_VIEWS.ADD.API_FOLDERS.ERROR_MESSAGE')
          );
        }
      }
      this.$emit('applyFilter', this.appliedFilters);
      this.$store.dispatch(
        'setConversationFilters',
        JSON.parse(JSON.stringify(this.appliedFilters))
      );
      this.$track(CONVERSATION_EVENTS.APPLY_FILTER, {
        applied_filters: this.appliedFilters.map(filter => ({
          key: filter.attribute_key,
          operator: filter.filter_operator,
          query_operator: filter.query_operator,
        })),
      });
    },
    updateSavedCustomViews() {
      if (this.editingCustomViewId) {
        const customView = this.customViews.find(
          view => view.id === this.editingCustomViewId
        );
        this.$store.dispatch('customViews/update', {
          ...customView,
          name: this.activeFolderNewName,
          query: filterQueryGenerator(this.appliedFilters),
        });
        this.showAlert(
          this.$t('FILTER.CUSTOM_VIEWS.ROW_ACTIONS.UPDATE_SUCCESS_MESSAGE')
        );
        this.editingCustomViewId = null;
        this.activeTab = 'saved_filters';
        return;
      }
      this.$emit('updateFolder', this.appliedFilters, this.activeFolderNewName);
    },
    selectTab(tab) {
      if (tab === 'new_filter' && this.activeTab !== 'new_filter') {
        this.editingCustomViewId = null;
      }
      this.activeTab = tab;
    },
    openCustomViewMenu(event, customView) {
      event.stopPropagation();
      const menuOffset = { he: 280, ar: 80 };
      this.contextMenuPosition = {
        x:
          (event.pageX || event.clientX) +
          (menuOffset[this.$root.$i18n.locale] || -160),
        y: event.pageY || event.clientY,
      };
      this.openMenuCustomViewId = customView.id;
    },
    closeCustomViewMenu() {
      this.openMenuCustomViewId = null;
    },
    onEditCustomView(customView) {
      this.closeCustomViewMenu();
      const query = customView.query?.payload;
      if (Array.isArray(query)) {
        const allCustomAttributes = this.$store.getters[
          'attributes/getAttributesByModel'
        ](this.attributeModel);
        const params = {
          agents: this.$store.getters['agents/getAgents'],
          teams: this.$store.getters['teams/getTeams'],
          inboxes: this.$store.getters['inboxes/getInboxes'],
          labels: this.$store.getters['labels/getLabels'],
          campaigns: this.$store.getters['campaigns/getAllCampaigns'],
          languages,
          countries,
          statuses: this.getDropdownValues('status'),
          filterTypes: this.filterTypes,
          allCustomAttributes,
        };
        this.appliedFilters = query.map(filter => ({
          attribute_key: filter.attribute_key,
          attribute_model: filter.attribute_model,
          filter_operator: filter.filter_operator,
          values: Array.isArray(filter.values)
            ? generateValuesForEditCustomViews(filter, params)
            : [],
          query_operator: filter.query_operator,
          custom_attribute_type: filter.custom_attribute_type,
        }));
      }
      this.editingCustomViewId = customView.id;
      this.activeFolderNewName = customView.name;
    },
    backToSavedFiltersList() {
      this.editingCustomViewId = null;
    },
    onDuplicateCustomView(customView) {
      this.closeCustomViewMenu();
      this.customViewPendingDuplication = customView;
      this.duplicateName = `${customView.name}${this.$t(
        'FILTER.CUSTOM_VIEWS.ROW_ACTIONS.DUPLICATE_SUFFIX'
      )}`;
      this.showDuplicateModal = true;
      this.$nextTick(() => this.$refs.duplicateNameInput?.focus());
    },
    closeDuplicateModal() {
      this.showDuplicateModal = false;
      this.customViewPendingDuplication = null;
      this.duplicateName = '';
    },
    async confirmDuplicateCustomView() {
      if (!this.duplicateName || this.isDuplicating) return;
      this.isDuplicating = true;
      try {
        await this.$store.dispatch('customViews/create', {
          name: this.duplicateName,
          filter_type: 'conversation',
          query: this.customViewPendingDuplication.query,
        });
        this.closeDuplicateModal();
        this.showAlert(
          this.$t('FILTER.CUSTOM_VIEWS.ROW_ACTIONS.DUPLICATE_SUCCESS_MESSAGE')
        );
      } catch (error) {
        this.showAlert(
          this.$t('FILTER.CUSTOM_VIEWS.ROW_ACTIONS.DUPLICATE_ERROR_MESSAGE')
        );
      } finally {
        this.isDuplicating = false;
      }
    },
    onDeleteCustomView(customView) {
      this.closeCustomViewMenu();
      this.customViewPendingDeletion = customView;
      this.showDeleteConfirmModal = true;
    },
    closeDeleteConfirmModal() {
      this.showDeleteConfirmModal = false;
      this.customViewPendingDeletion = null;
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
    onChangeSaveFitler(event) {
      this.isSaveFitler = event.target.checked;
    },
  },
};
</script>
<style lang="scss">
.duplicate-filter-modal {
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);

  > .modal-container {
    @apply rounded-[12px] w-[554px] max-w-[90%];
  }
}

.conversation-advanced-filter-modal {
  > .modal-container {
    @apply rounded-[12px] w-[700px] max-w-[90%] min-h-[526px] flex flex-col;
  }

  > .modal-container > .conv-advanced-filter {
    @apply flex flex-col grow;

    > * {
      @apply shrink-0;
    }
  }

  .filters-body,
  .saved-filters-body {
    @apply grow shrink;
  }
}
</style>
<style lang="scss" scoped>
::v-deep {
  .apply-filter-btn,
  .cancel-filter-btn {
    height: unset;
    .button__content {
      line-height: 22px !important;
    }
  }
}
.folder-input {
  @apply w-[50%] rounded-[8px] border-gray-300 h-[44px] px-3 py-[10px] hover:border-gray-400 focus:border-gray-400;
}
.saved-filter-row {
  @apply flex flex-row items-center justify-between text-gray-600;
  @apply w-full rounded-[4px] px-1 py-2 cursor-pointer hover:bg-[#F0FDF4];

  &.selected {
    @apply bg-[#F0FDF4];
  }
}
.filters-body {
  @apply flex flex-col justify-between;
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full;
  }

  &::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
}

.tabs {
  .tab-item {
    @apply bg-white border-[#F3F4F6] text-gray-700;

    &:hover {
      @apply bg-green-50 border-green-500 text-green-600;
    }

    &.active {
      @apply bg-green-50 border-green-500 text-green-700;
    }
  }
}

.menu-container {
  @apply bg-white shadow-xl rounded-lg;
  min-width: 175px;

  div:first-child {
    @apply rounded-t-lg;
  }

  div:last-child {
    @apply rounded-b-lg rounded-t-none;
  }

  ::v-deep .menu {
    @apply flex items-center gap-1 rounded-none cursor-pointer;
    width: 100%;
    height: 28px;
    padding: 4px 8px;

    .menu-label {
      font-size: 14px !important;
    }

    &:hover {
      @apply bg-green-50 text-[#164B35];

      .material-symbols-outlined,
      .menu-label {
        @apply text-[#164B35];
      }
    }

    .menu-icon {
      @apply flex-shrink-0 text-slate-600;
    }

    .menu-label {
      @apply m-0 leading-4 text-slate-600;
    }
  }
}

.danger-item {
  ::v-deep .menu {
    .menu-icon,
    .menu-label {
      @apply text-red-600;
    }

    &:hover {
      @apply bg-red-50;

      .material-symbols-outlined,
      .menu-label {
        @apply text-red-600;
      }
    }
  }
}
</style>
